import { useCallback, useEffect, useMemo, useState } from "react";
import { X, Loader2, Search, Shield, ChevronDown, ChevronRight, Crown, Save, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { LEVEL_LABELS, LEVEL_RANK, MODULE_CATEGORIES } from "@/lib/permissions";

/**
 * iter157 — PermissionsDrawer
 *
 * The one and only UI to edit per-user permission overrides. Backed by
 * `PATCH /users/{id}/permissions`. Owner accounts render read-only —
 * their permissions are unconditionally Full Control and cannot be
 * modified (backend also enforces).
 */
const LEVEL_OPTIONS = [
  { value: "no_access", label: "No Access", tone: "text-stone-500" },
  { value: "view_only", label: "View Only", tone: "text-blue-700" },
  { value: "edit",      label: "Edit",      tone: "text-amber-700" },
  { value: "full_control", label: "Full Control", tone: "text-emerald-700" },
];

export default function PermissionsDrawer({ userId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [effective, setEffective] = useState({});
  const [q, setQ] = useState("");
  const [openCategories, setOpenCategories] = useState(() =>
    Object.fromEntries(MODULE_CATEGORIES.map((c) => [c.name, true])),
  );

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${userId}/permissions`);
      setUser(data);
      setOverrides(data.overrides || {});
      setEffective(data.effective || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load permissions");
      onClose?.();
    } finally { setLoading(false); }
  }, [userId, onClose]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isOwner = !!user?.is_owner;
  const readOnly = isOwner;

  const setLevel = (slug, level) => {
    if (readOnly) return;
    setOverrides((prev) => ({ ...prev, [slug]: level }));
  };

  const setCategoryLevel = (categoryName, level) => {
    if (readOnly) return;
    const cat = MODULE_CATEGORIES.find((c) => c.name === categoryName);
    if (!cat) return;
    setOverrides((prev) => {
      const next = { ...prev };
      cat.modules.forEach((m) => { next[m.slug] = level; });
      return next;
    });
  };

  const filteredCategories = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return MODULE_CATEGORIES;
    return MODULE_CATEGORIES
      .map((c) => ({
        ...c,
        modules: c.modules.filter((m) =>
          m.label.toLowerCase().includes(t) || m.slug.includes(t) || c.name.toLowerCase().includes(t)
        ),
      }))
      .filter((c) => c.modules.length > 0);
  }, [q]);

  const save = async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/users/${userId}/permissions`, { overrides });
      setEffective(data.effective || {});
      toast.success("Permissions updated");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const currentLevel = (slug) => overrides[slug] ?? effective[slug] ?? "no_access";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="permissions-drawer"
    >
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="flex items-start justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">User Management</div>
            <h2 className="font-heading text-xl font-semibold text-stone-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-rose-700" strokeWidth={1.8} /> Permissions
            </h2>
            {user && (
              <div className="text-xs text-stone-500 mt-1 space-x-1">
                <span className="font-medium text-stone-700" data-testid="permissions-drawer-email">{user.email}</span>
                <span>·</span>
                <span className="uppercase tracking-wider text-[10px] font-semibold">{user.role}</span>
                {isOwner && (
                  <span className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-widest" data-testid="permissions-drawer-owner-badge">
                    <Crown className="w-3 h-3" strokeWidth={2.2} /> Owner
                  </span>
                )}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded hover:bg-stone-100" aria-label="Close" data-testid="permissions-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
          </div>
        ) : (
          <>
            {readOnly && (
              <div className="mx-6 mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2" data-testid="permissions-drawer-owner-notice">
                <Info className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
                Owner accounts always have Full Control on every module. Permissions cannot be reduced.
              </div>
            )}

            <div className="px-6 pt-4 pb-2 border-b border-stone-100 sticky top-[73px] bg-white z-10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                <input
                  type="text"
                  placeholder="Search modules…"
                  className="input pl-8 w-full text-sm"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="permissions-drawer-search"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {filteredCategories.length === 0 && (
                <div className="text-center text-stone-400 text-sm py-10" data-testid="permissions-drawer-empty">
                  No modules match "{q}".
                </div>
              )}
              {filteredCategories.map((cat) => {
                const open = openCategories[cat.name] !== false;
                return (
                  <div key={cat.name} className="rounded-xl border border-stone-200 overflow-hidden" data-testid={`permissions-cat-${cat.name}`}>
                    <button
                      type="button"
                      onClick={() => setOpenCategories((s) => ({ ...s, [cat.name]: !open }))}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-stone-50 hover:bg-stone-100 transition"
                      data-testid={`permissions-cat-toggle-${cat.name}`}
                    >
                      <div className="flex items-center gap-2">
                        {open ? <ChevronDown className="w-4 h-4 text-stone-500" /> : <ChevronRight className="w-4 h-4 text-stone-500" />}
                        <span className="font-heading font-semibold text-sm text-stone-900">{cat.name}</span>
                        <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                          {cat.modules.length} module{cat.modules.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      {!readOnly && (
                        <select
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v) setCategoryLevel(cat.name, v);
                            e.target.value = "";
                          }}
                          className="text-xs px-2 py-1 rounded border border-stone-200 bg-white"
                          data-testid={`permissions-cat-bulk-${cat.name}`}
                        >
                          <option value="">Set all…</option>
                          {LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      )}
                    </button>
                    {open && (
                      <div className="divide-y divide-stone-100">
                        {cat.modules.map((m) => {
                          const level = currentLevel(m.slug);
                          return (
                            <div key={m.slug} className="flex items-center justify-between px-4 py-2.5" data-testid={`permissions-module-${m.slug}`}>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-stone-800">{m.label}</div>
                                <div className="text-[10px] uppercase tracking-widest text-stone-400 font-mono">{m.slug}</div>
                              </div>
                              <select
                                disabled={readOnly}
                                value={level}
                                onChange={(e) => setLevel(m.slug, e.target.value)}
                                className={`text-xs px-2 py-1.5 rounded border border-stone-200 bg-white font-semibold min-w-[130px] ${LEVEL_OPTIONS.find((o) => o.value === level)?.tone || ""}`}
                                data-testid={`permissions-select-${m.slug}`}
                              >
                                {LEVEL_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="px-6 py-3 border-t border-stone-200 flex items-center justify-between gap-2 bg-white sticky bottom-0">
              <div className="text-[11px] text-stone-500">
                Changes are audited.{" "}
                <span className="text-stone-400">Backend enforces these rules independently.</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn btn-outline btn-sm"
                  data-testid="permissions-drawer-cancel"
                >
                  Cancel
                </button>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="btn btn-primary btn-sm inline-flex items-center gap-1"
                    data-testid="permissions-drawer-save"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Permissions
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
